/**
 * InlineRawMaterialLinking — inline UI for adding raw material linkages
 * to a finished product that has none. Shown in the session creation modal
 * when the selected product has no production_inputs entries.
 */
import React, { useState, useRef, useCallback } from 'react';
import { Plus, Search, X, Loader2, Link2, ChevronUp, ChevronDown } from 'lucide-react';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useLanguage } from '../../../../i18n/LanguageContext';
import inputOutputMappingService, { type RawMaterialSearchResult } from '../../../../services/inputOutputMappingService';

export interface PendingMaterial {
  item_number: string;
  item_english_name: string | null;
  item_chinese_name: string | null;
  preference: number;
}

interface InlineRawMaterialLinkingProps {
  productSku: string;
  productName: string;
  onPendingMaterialsChange: (materials: PendingMaterial[]) => void;
}

const InlineRawMaterialLinking: React.FC<InlineRawMaterialLinkingProps> = ({
  productSku,
  productName,
  onPendingMaterialsChange,
}) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<RawMaterialSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linkedMaterials, setLinkedMaterials] = useState<PendingMaterial[]>([]);
  const [showPreference, setShowPreference] = useState(false);
  const [pendingMaterial, setPendingMaterial] = useState<RawMaterialSearchResult | null>(null);
  const [preferenceValue, setPreferenceValue] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback((text: string) => {
    setSearchText(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await inputOutputMappingService.searchRawMaterials(text.trim());
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  const handleSelectMaterial = (item: RawMaterialSearchResult) => {
    // If this is the first material, auto-assign preference 1
    if (linkedMaterials.length === 0) {
      linkMaterial(item, 1);
    } else {
      // Show preference input for subsequent materials
      setPendingMaterial(item);
      setPreferenceValue(String(linkedMaterials.length + 1));
      setShowPreference(true);
    }
  };

  const confirmPreference = () => {
    if (!pendingMaterial) return;
    const pref = parseInt(preferenceValue) || linkedMaterials.length + 1;

    // Bump existing materials with preference >= new value
    const bumped = linkedMaterials.map(m =>
      m.preference >= pref ? { ...m, preference: m.preference + 1 } : m
    );

    linkMaterial(pendingMaterial, pref, bumped);
    setShowPreference(false);
    setPendingMaterial(null);
    setPreferenceValue('');
  };

  const linkMaterial = async (item: RawMaterialSearchResult, preference: number, baseList?: PendingMaterial[]) => {
    setSaving(true);
    try {
      // Save linkage immediately via API
      await inputOutputMappingService.createMapping({
        product_item_number: productSku,
        raw_material_item_number: item.item_number,
        preference,
      });

      const newMaterial: PendingMaterial = {
        item_number: item.item_number,
        item_english_name: item.item_english_name,
        item_chinese_name: item.item_chinese_name,
        preference,
      };
      const updated = [...(baseList || linkedMaterials), newMaterial];
      setLinkedMaterials(updated);
      onPendingMaterialsChange(updated);
      setSearchText('');
      setSearchResults([]);

      // Refresh raw materials so yield, unit_weight, stock populate
      await queryClient.invalidateQueries(['rawMaterials', productSku]);
      await queryClient.invalidateQueries(['products']);
      toast.success(t('supervisor.materialLinkSuccess') || 'Raw material linked successfully');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t('supervisor.materialLinkError') || 'Failed to link material');
    } finally {
      setSaving(false);
    }
  };

  const removeMaterial = async (itemNumber: string) => {
    try {
      await inputOutputMappingService.deleteMapping({
        product_item_number: productSku,
        raw_material_item_number: itemNumber,
      });
      const updated = linkedMaterials.filter(m => m.item_number !== itemNumber);
      setLinkedMaterials(updated);
      onPendingMaterialsChange(updated);
      await queryClient.invalidateQueries(['rawMaterials', productSku]);
      await queryClient.invalidateQueries(['products']);
      toast.success(t('supervisor.materialUnlinked') || 'Raw material removed');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to remove material');
    }
  };

  const moveMaterial = async (itemNumber: string, direction: 'up' | 'down') => {
    const sorted = [...linkedMaterials].sort((a, b) => a.preference - b.preference);
    const idx = sorted.findIndex(m => m.item_number === itemNumber);
    if (idx < 0) return;
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === sorted.length - 1) return;

    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const thisItem = sorted[idx];
    const otherItem = sorted[swapIdx];

    // Swap preferences
    const updated = linkedMaterials.map(m => {
      if (m.item_number === thisItem.item_number) return { ...m, preference: otherItem.preference };
      if (m.item_number === otherItem.item_number) return { ...m, preference: thisItem.preference };
      return m;
    });
    setLinkedMaterials(updated);
    onPendingMaterialsChange(updated);

    try {
      await Promise.all([
        inputOutputMappingService.updatePreference({
          product_item_number: productSku,
          raw_material_item_number: thisItem.item_number,
          preference: otherItem.preference,
        }),
        inputOutputMappingService.updatePreference({
          product_item_number: productSku,
          raw_material_item_number: otherItem.item_number,
          preference: thisItem.preference,
        }),
      ]);
      await queryClient.invalidateQueries(['rawMaterials', productSku]);
    } catch {
      toast.error('Failed to update preference');
    }
  };

  // Filter out already-linked materials from search results
  const linkedSkus = new Set(linkedMaterials.map(m => m.item_number));
  const filteredResults = searchResults.filter(r => !linkedSkus.has(r.item_number));

  return (
    <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg">
      <div className="flex items-center gap-2 mb-3">
        <Link2 className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
          {t('supervisor.noMaterialsLinked') || 'No raw materials linked to this product'}
        </span>
      </div>
      <p className="text-xs text-amber-700 dark:text-amber-400 mb-3">
        {t('supervisor.addMaterialsPrompt') || 'Search and add raw materials to continue creating a session.'}
      </p>

      {/* Already linked materials */}
      {linkedMaterials.length > 0 && (
        <div className="mb-3 space-y-1">
          {[...linkedMaterials].sort((a, b) => a.preference - b.preference).map(m => (
            <div
              key={m.item_number}
              className="flex items-center justify-between px-2 py-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded text-sm"
            >
              <div className="flex items-center gap-1">
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => moveMaterial(m.item_number, 'up')}
                    className="p-0 text-gray-400 hover:text-green-600 dark:hover:text-green-400 disabled:opacity-30"
                    disabled={[...linkedMaterials].sort((a, b) => a.preference - b.preference)[0]?.item_number === m.item_number}
                  >
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveMaterial(m.item_number, 'down')}
                    className="p-0 text-gray-400 hover:text-green-600 dark:hover:text-green-400 disabled:opacity-30"
                    disabled={(() => { const s = [...linkedMaterials].sort((a, b) => a.preference - b.preference); return s[s.length - 1]?.item_number === m.item_number; })()}
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <span className="w-5 h-5 flex items-center justify-center bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 rounded-full text-xs font-medium">
                  {m.preference}
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-xs">{m.item_number}</span>
                <span className="text-gray-900 dark:text-white">
                  {m.item_english_name || m.item_number}
                </span>
              </div>
              <button
                type="button"
                onClick={() => removeMaterial(m.item_number)}
                className="p-0.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Preference input dialog */}
      {showPreference && pendingMaterial && (
        <div className="mb-3 p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg">
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
            {t('supervisor.setPreference') || 'Set preference for'}{' '}
            <span className="font-medium">{pendingMaterial.item_english_name || pendingMaterial.item_number}</span>
          </p>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              value={preferenceValue}
              onChange={e => setPreferenceValue(e.target.value)}
              className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') confirmPreference(); }}
            />
            <button
              type="button"
              onClick={confirmPreference}
              className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              {t('common.confirm') || 'Confirm'}
            </button>
            <button
              type="button"
              onClick={() => { setShowPreference(false); setPendingMaterial(null); }}
              className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              {t('common.cancel') || 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchText}
          onChange={e => handleSearch(e.target.value)}
          placeholder={t('supervisor.searchRawMaterials') || 'Search raw materials by name or SKU...'}
          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
        />
        {searching && (
          <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
      </div>

      {/* Search results */}
      {filteredResults.length > 0 && (
        <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
          {filteredResults.map(item => (
            <button
              key={item.item_number}
              type="button"
              onClick={() => handleSelectMaterial(item)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0 flex items-center justify-between"
            >
              <div className="text-gray-900 dark:text-white">
                <span>{item.item_number}</span>
                {item.item_english_name && (
                  <span className="ml-2">{item.item_english_name}</span>
                )}
                {item.item_chinese_name && (
                  <span className="ml-2">{item.item_chinese_name}</span>
                )}
                {item.packing && (
                  <span className="ml-2">{item.packing}</span>
                )}
              </div>
              <Plus className="w-4 h-4 text-indigo-500 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {searchText.trim().length >= 2 && !searching && filteredResults.length === 0 && searchResults.length === 0 && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {t('common.noResults') || 'No results found'}
        </p>
      )}
    </div>
  );
};

export default InlineRawMaterialLinking;
